/**
 * Before/after image comparison slider.
 *
 * Pointer events give one code path for mouse, touch and pen. Moves are cached
 * and flushed on the next animation frame, so a burst of pointermove events
 * produces one style write per frame rather than one per event.
 */
class BeforeAfterSlider extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('.before-after__slider');
    if (!this.slider) return;

    this.rect = null;
    this.frame = null;
    this.pendingX = 0;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.flush = this.flush.bind(this);

    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.slider.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  onPointerDown(event) {
    // Measure once per drag: reading layout on every move is what stutters.
    this.rect = this.slider.getBoundingClientRect();

    this.slider.setPointerCapture(event.pointerId);
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerUp);
    this.slider.addEventListener('pointercancel', this.onPointerUp);
    this.setAttribute('dragging', '');

    this.onPointerMove(event);
  }

  onPointerMove(event) {
    event.preventDefault();

    this.pendingX = event.clientX;
    if (this.frame) return;

    this.frame = requestAnimationFrame(this.flush);
  }

  flush() {
    this.frame = null;
    if (!this.rect || !this.rect.width) return;

    const offset = this.pendingX - this.rect.left;
    const clamped = Math.max(0, Math.min(offset, this.rect.width));

    this.setPercent((clamped / this.rect.width) * 100);
  }

  onPointerUp(event) {
    this.slider.releasePointerCapture(event.pointerId);
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider.removeEventListener('pointercancel', this.onPointerUp);
    this.removeAttribute('dragging');
  }

  onKeydown(event) {
    const step = event.shiftKey ? 10 : 2;
    const current = parseFloat(this.style.getPropertyValue('--percent')) || 50;

    if (event.key === 'ArrowLeft') this.setPercent(current - step);
    else if (event.key === 'ArrowRight') this.setPercent(current + step);
    else if (event.key === 'Home') this.setPercent(0);
    else if (event.key === 'End') this.setPercent(100);
    else return;

    event.preventDefault();
  }

  setPercent(value) {
    const percent = Math.max(0, Math.min(value, 100));

    this.style.setProperty('--percent', `${percent}%`);
    this.slider.setAttribute('aria-valuenow', Math.round(percent));
  }
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}
